import React from "react"
import { Link } from "react-router-dom"

export function CaseStudyTemplate({
  eyebrow = "Case study",
  title,
  tag,
  image,
  imageAlt,
  /** Optional second visual (e.g. alternate mockup) shown below the hero image */
  secondaryImage,
  secondaryImageAlt,
  summary,
  sections,
  tech,
  results,
  /** Scroll target on home (Selected Work block id), e.g. "selected-work-sheetbills" */
  backToAnchorId,
  liveSiteUrl,
  liveSiteLabel = "Visit live site",
}) {
  const whatsappUrl = "https://wa.me/18763932065"

  return (
    <section className="bg-white" style={{ paddingTop: "112px", paddingBottom: "88px" }}>
      <style>{`
        .cs-tag {
          display: inline-block;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          padding: 4px 12px;
          border: 1px solid #d4d4d4;
          border-radius: 999px;
          color: #666;
        }

        .cs-divider {
          width: 100%;
          height: 1px;
          background: #efefef;
        }

        .cs-hero-img {
          overflow: hidden;
          border-radius: 14px;
          border: 1px solid #efefef;
          background: #fafaf9;
        }

        .cs-hero-img img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
        }

        .cs-kicker {
          font-size: 12px;
          color: #999;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          font-weight: 700;
        }

        .cs-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 16px;
          border-radius: 10px;
          border: 1px solid #111;
          background: #111;
          color: #fff;
          text-decoration: none;
          font-size: 13px;
          font-weight: 700;
          transition: opacity 0.2s;
          white-space: nowrap;
        }

        .cs-btn:hover { opacity: 0.78; }

        .cs-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 600;
          color: #111;
          text-decoration: none;
          border-bottom: 1px solid #111;
          padding-bottom: 2px;
          transition: opacity 0.2s;
        }

        .cs-link:hover { opacity: 0.6; }

        .cs-section-title {
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #999;
          margin-bottom: 10px;
        }

        .cs-body {
          color: #555;
          line-height: 1.85;
          font-size: 15px;
        }

        .cs-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 8px 10px;
          border-radius: 999px;
          border: 1px solid #ededed;
          background: #fff;
          color: #444;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.01em;
        }
      `}</style>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-16">
        <div className="flex flex-wrap items-center justify-between gap-12 mb-14">
          <div style={{ display: "flex", flexDirection: "column", gap: "14px", maxWidth: "720px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
              <span className="cs-tag">{eyebrow}</span>
              {tag ? <span className="cs-tag" style={{ borderColor: "#efefef", color: "#444" }}>{tag}</span> : null}
            </div>
            <h1 style={{ fontWeight: 800, fontSize: "clamp(28px, 4vw, 52px)", lineHeight: 1.08, color: "#111" }}>
              {title}
            </h1>
            {summary ? (
              <p className="cs-body" style={{ fontSize: "16px", maxWidth: "62ch" }}>
                {summary}
              </p>
            ) : null}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
              <a className="cs-btn" href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                Book service on WhatsApp
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M7 17L17 7M17 7H7M17 7v10" />
                </svg>
              </a>
              {liveSiteUrl ? (
                <a
                  className="cs-btn"
                  href={liveSiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ background: "#fff", color: "#111", border: "1px solid #111" }}
                >
                  {liveSiteLabel}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                  </svg>
                </a>
              ) : null}
              <Link
                className="cs-link"
                to={backToAnchorId ? `/#${backToAnchorId}` : "/"}
              >
                Back to portfolio
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M19 12H5M5 12l7 7M5 12l7-7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>

        <div className="cs-hero-img aspect-[16/9] mb-14">
          <img src={image} alt={imageAlt || title} />
        </div>

        {secondaryImage ? (
          <div className="cs-hero-img aspect-[16/9] mb-14">
            <img src={secondaryImage} alt={secondaryImageAlt || ""} />
          </div>
        ) : null}

        <div className="cs-divider mb-14" />

        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-14 items-start">
          <div style={{ display: "flex", flexDirection: "column", gap: "26px" }}>
            {sections?.map((s) => (
              <div key={s.title}>
                <div className="cs-section-title">{s.title}</div>
                <div className="cs-body">{s.body}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
            <div style={{ border: "1px solid #efefef", borderRadius: "14px", padding: "18px 18px", background: "#fafaf9" }}>
              <div className="cs-section-title">Highlights</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {(results || []).map((r) => (
                  <span key={r} className="cs-pill">{r}</span>
                ))}
              </div>
            </div>

            <div style={{ border: "1px solid #efefef", borderRadius: "14px", padding: "18px 18px", background: "#fff" }}>
              <div className="cs-section-title">Tools</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {(tech || []).map((t) => (
                  <span key={t} className="cs-pill">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

